/**
 * @fileoverview Service Worker Manager - Cache invalidation and versioning
 * 
 * @description
 * Manages service worker lifecycle and cache invalidation to handle
 * deployment updates gracefully. Part of the cache-busting architecture
 * that prevents "Failed to fetch dynamically imported module" errors.
 * 
 * ## Architecture
 * ```
 * New Deployment → build-manifest.json updated
 *                         ↓
 *              checkCacheValidity() detects mismatch
 *                         ↓
 *              invalidateAllCaches() triggered
 *                         ↓
 * ┌─────────────────────────────────────────┐
 * │ • Clear all Cache Storage              │
 * │ • Unregister Service Workers           │
 * │ • Clear sessionStorage flags           │
 * │ • User prompted to refresh             │
 * └─────────────────────────────────────────┘
 * ```
 * 
 * ## Key Functions
 * | Function                  | Purpose                              |
 * |---------------------------|--------------------------------------|
 * | clearServiceWorkerCaches  | Delete all Cache API entries         |
 * | unregisterServiceWorkers  | Remove all SW registrations          |
 * | skipWaitingServiceWorker  | Force waiting SW to activate         |
 * | invalidateAllCaches       | Full cache wipe                      |
 * | checkCacheValidity        | Compare build versions               |
 * | initializeCacheManager    | Startup cache validation             |
 * 
 * @example
 * ```ts
 * // On app startup
 * import { initializeCacheManager } from '@/lib/serviceWorkerManager';
 * 
 * useEffect(() => {
 *   initializeCacheManager();
 * }, []);
 * 
 * // Force refresh when stale detected
 * if (sessionStorage.getItem('stale_chunks_detected')) {
 *   await invalidateAllCaches();
 *   window.location.reload();
 * }
 * ```
 * 
 * @module lib/serviceWorkerManager
 * @see lazyWithRetry - Companion for chunk loading
 * @see VersionChecker - UI component for update prompts
 */

/** Session storage key for tracking current cache version */
const SW_CACHE_VERSION_KEY = 'app_cache_version';

/**
 * Check if there's an active service worker and clear its caches
 */
export async function clearServiceWorkerCaches(): Promise<boolean> {
  if (!('caches' in window)) {
    console.debug('[SW Manager] Cache API not available');
    return false;
  }

  try {
    const cacheNames = await caches.keys();
    
    if (cacheNames.length === 0) {
      console.debug('[SW Manager] No caches to clear');
      return true;
    }

    console.info('[SW Manager] Clearing caches:', cacheNames);
    
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );

    console.info('[SW Manager] All caches cleared');
    return true;
  } catch (error) {
    console.error('[SW Manager] Failed to clear caches:', error);
    return false;
  }
}

/**
 * Unregister all service workers
 */
export async function unregisterServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    console.debug('[SW Manager] Service Worker not supported');
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length === 0) {
      console.debug('[SW Manager] No service workers to unregister');
      return true;
    }

    console.info('[SW Manager] Unregistering service workers:', registrations.length);
    
    await Promise.all(
      registrations.map(registration => registration.unregister())
    );

    console.info('[SW Manager] All service workers unregistered');
    return true;
  } catch (error) {
    console.error('[SW Manager] Failed to unregister service workers:', error);
    return false;
  }
}

/**
 * Force update any waiting service worker
 */
export async function skipWaitingServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      console.info('[SW Manager] Sent SKIP_WAITING to service worker');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[SW Manager] Failed to skip waiting:', error);
    return false;
  }
}

/**
 * Full cache invalidation - clears everything and forces refresh
 */
export async function invalidateAllCaches(): Promise<void> {
  console.info('[SW Manager] Starting full cache invalidation');
  
  // Clear caches
  await clearServiceWorkerCaches();
  
  // Unregister service workers
  await unregisterServiceWorkers();
  
  // Clear stored version
  sessionStorage.removeItem(SW_CACHE_VERSION_KEY);
  sessionStorage.removeItem('app_build_version');
  sessionStorage.removeItem('stale_chunks_detected');
  
  console.info('[SW Manager] Cache invalidation complete');
}

/**
 * Check if cache needs invalidation based on build version
 */
export async function checkCacheValidity(): Promise<{ isValid: boolean; currentVersion?: string; storedVersion?: string }> {
  try {
    const response = await fetch(`/build-manifest.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });

    if (!response.ok) {
      return { isValid: true }; // Can't verify, assume valid
    }

    const manifest = await response.json();
    const currentVersion = manifest.version;
    const storedVersion = sessionStorage.getItem(SW_CACHE_VERSION_KEY);

    if (!storedVersion) {
      // First visit, store version
      sessionStorage.setItem(SW_CACHE_VERSION_KEY, currentVersion);
      return { isValid: true, currentVersion };
    }

    if (storedVersion !== currentVersion) {
      console.info('[SW Manager] Version mismatch detected', {
        stored: storedVersion,
        current: currentVersion
      });
      return { isValid: false, currentVersion, storedVersion };
    }

    return { isValid: true, currentVersion, storedVersion };
  } catch (error) {
    console.debug('[SW Manager] Version check failed:', error);
    return { isValid: true }; // Can't verify, assume valid
  }
}

/**
 * Initialize cache management on app startup
 */
export async function initializeCacheManager(): Promise<void> {
  const { isValid, currentVersion, storedVersion } = await checkCacheValidity();

  if (!isValid) {
    console.warn('[SW Manager] Stale cache detected, invalidating...', {
      current: currentVersion,
      stored: storedVersion
    });
    
    await invalidateAllCaches();
    
    // Store new version after invalidation
    if (currentVersion) {
      sessionStorage.setItem(SW_CACHE_VERSION_KEY, currentVersion);
    }
  }
}
